import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const roleBadgeColor = { MANAGER: '#3B82F6', STAFF: '#10B981' };

const s = {
  page: {
    minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center',
    background: '#0F172A', fontFamily: 'system-ui, sans-serif', padding: '2rem 1rem',
  },
  wrapper: { width: '100%', maxWidth: 560 },
  progress: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    marginBottom: '2rem',
  },
  stepDot: (active, done) => ({
    width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem',
    background: active ? '#7C3AED' : done ? '#10B981' : '#334155',
    color: active || done ? '#fff' : '#94A3B8',
  }),
  stepLine: (done) => ({
    width: 40, height: 2, background: done ? '#10B981' : '#334155',
  }),
  stepLabel: { textAlign: 'center', fontSize: '0.8rem', color: '#94A3B8', marginBottom: '1.5rem' },
  card: {
    background: '#1E293B', borderRadius: 12, padding: '1.5rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)', marginBottom: '1rem', border: '1px solid #334155',
  },
  title: { margin: '0 0 1.25rem', fontSize: '1.3rem', fontWeight: 700, color: '#F8FAFC' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#94A3B8', marginBottom: 4 },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', boxSizing: 'border-box', outline: 'none', marginBottom: '1rem',
    background: '#1E293B', color: '#F8FAFC',
  },
  textarea: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', boxSizing: 'border-box', outline: 'none', marginBottom: '1rem',
    resize: 'vertical', minHeight: 60, fontFamily: 'inherit',
    background: '#1E293B', color: '#F8FAFC',
  },
  timeInput: {
    padding: '0.4rem 0.5rem', borderRadius: 6, border: '1.5px solid #334155',
    fontSize: '0.85rem', outline: 'none', background: '#1E293B', color: '#F8FAFC',
  },
  dayRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.6rem', flexWrap: 'wrap' },
  dayLabel: { width: 90, fontWeight: 600, fontSize: '0.85rem', color: '#F8FAFC' },
  closedLabel: { fontSize: '0.8rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 4 },
  btn: {
    padding: '0.75rem 1.5rem', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
  },
  btnOutline: {
    padding: '0.75rem 1.5rem', borderRadius: 8, border: '1.5px solid #334155',
    background: 'transparent', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', color: '#94A3B8',
  },
  btnSmall: {
    padding: '0.5rem 1rem', borderRadius: 6, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  },
  btnDanger: {
    padding: '0.4rem 0.75rem', borderRadius: 6, border: 'none',
    background: '#FEE2E2', color: '#DC2626', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  },
  row: { display: 'flex', gap: '0.75rem', marginTop: '0.5rem' },
  error: { color: '#EF4444', fontSize: '0.85rem', marginBottom: '0.75rem' },
  svcItem: {
    display: 'flex', alignItems: 'center', padding: '0.75rem 0',
    borderBottom: '1px solid #334155', gap: '0.75rem',
  },
  inviteItem: {
    display: 'flex', alignItems: 'center', padding: '0.75rem 0',
    borderBottom: '1px solid #334155', gap: '0.75rem',
  },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    fontSize: '0.7rem', fontWeight: 700, background: color, color: '#fff',
  }),
  select: {
    padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', outline: 'none', background: '#1E293B', color: '#F8FAFC',
  },
  note: { fontSize: '0.8rem', color: '#94A3B8', marginTop: '0.75rem' },
  skipBtn: {
    background: 'none', border: 'none', color: '#94A3B8', fontSize: '0.85rem',
    cursor: 'pointer', textDecoration: 'underline', display: 'block',
    margin: '0 auto 1.5rem', padding: 0,
  },
  footer: { display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' },
};

const defaultHours = () => DAY_KEYS.map((_, i) => ({
  dayOfWeek: i,
  openTime: '09:00',
  closeTime: '17:00',
  closed: i >= 5, // Sat+Sun closed
}));

export default function SetupWizardPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Step 1 state
  const [shopName, setShopName] = useState('');
  const [shopDesc, setShopDesc] = useState('');
  const [hours, setHours] = useState(defaultHours);
  const [shopId, setShopId] = useState(null);

  // Step 2 state
  const [services, setServices] = useState([]);
  const [svcName, setSvcName] = useState('');
  const [svcDesc, setSvcDesc] = useState('');

  // Step 3 state
  const [invites, setInvites] = useState([]);
  const [invEmail, setInvEmail] = useState('');
  const [invRole, setInvRole] = useState('STAFF');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // ── Step 1: Create Shop ─────────────────────────────────────────────────────
  const submitShop = async () => {
    if (!shopName.trim()) { setError(t('setup.errors.shopNameRequired')); return; }
    setLoading(true); setError(null);
    try {
      // Create shop
      const res = await fetch(`${API_BASE}/api/v1/shops`, {
        method: 'POST', headers, body: JSON.stringify({ name: shopName.trim(), description: shopDesc.trim(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      });
      if (!res.ok) { setError(t('setup.errors.failedCreateShop')); return; }
      const shop = await res.json();
      setShopId(shop.id);

      // Save business hours
      await fetch(`${API_BASE}/api/v1/shops/${shop.id}/business-hours`, {
        method: 'PUT', headers, body: JSON.stringify({ entries: hours }),
      });

      setStep(2);
    } catch { setError(t('setup.errors.network')); } finally { setLoading(false); }
  };

  // ── Step 2: Add Service ─────────────────────────────────────────────────────
  const addService = async () => {
    if (!svcName.trim()) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/services`, {
        method: 'POST', headers,
        body: JSON.stringify({ shopId, name: svcName.trim(), description: svcDesc.trim() }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.message || t('setup.errors.failedAddService')); return; }
      const svc = await res.json();
      setServices((prev) => [...prev, svc]);
      setSvcName(''); setSvcDesc('');
    } catch { setError(t('setup.errors.network')); }
  };

  const removeService = async (svcId) => {
    await fetch(`${API_BASE}/api/v1/services/${svcId}`, { method: 'DELETE', headers });
    setServices((prev) => prev.filter((sv) => sv.id !== svcId));
  };

  // ── Step 3: Invite Staff ────────────────────────────────────────────────────
  const sendInvite = async () => {
    if (!invEmail.trim()) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/invites`, {
        method: 'POST', headers,
        body: JSON.stringify({ email: invEmail.trim(), role: invRole }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.message || t('setup.errors.failedInvite')); return; }
      setInvites((prev) => [...prev, { email: invEmail.trim(), role: invRole }]);
      setInvEmail('');
    } catch { setError(t('setup.errors.network')); }
  };

  const updateHour = (i, field, value) => setHours((h) => h.map((d, j) => (j === i ? { ...d, [field]: value } : d)));

  const stepLabels = [t('setup.steps.createShop'), t('setup.steps.addServices'), t('setup.steps.inviteTeam')];

  return (
    <div style={s.page}>
      <div style={s.wrapper}>
        {/* Progress indicator */}
        <div style={s.progress}>
          {[1, 2, 3].map((n, i) => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {i > 0 && <div style={s.stepLine(step > n - 1)} />}
              <div style={s.stepDot(step === n, step > n)}>{step > n ? '✓' : n}</div>
            </div>
          ))}
        </div>
        <p style={s.stepLabel}>{t('setup.stepOf', { step, total: 3 })} — {stepLabels[step - 1]}</p>
        <button style={s.skipBtn} onClick={() => navigate('/admin/queue')}>{t('setup.skipSetup')}</button>

        {error && <p style={s.error}>{error}</p>}

        {/* ── Step 1: Shop + Hours ───────────────────────────────────────── */}
        {step === 1 && (
          <div style={s.card}>
            <h2 style={s.title}>{t('setup.step1.title')}</h2>
            <label style={s.label}>{t('setup.step1.shopName')}</label>
            <input style={s.input} value={shopName} onChange={(e) => setShopName(e.target.value)} placeholder={t('setup.step1.shopNamePlaceholder')} />
            <label style={s.label}>{t('setup.step1.description')}</label>
            <textarea style={s.textarea} value={shopDesc} onChange={(e) => setShopDesc(e.target.value)} placeholder={t('setup.step1.descriptionPlaceholder')} />

            <label style={{ ...s.label, marginTop: '0.5rem', marginBottom: '0.75rem' }}>{t('setup.step1.businessHours')}</label>
            {hours.map((d, i) => (
              <div key={i} style={s.dayRow}>
                <span style={s.dayLabel}>{t(`setup.days.${DAY_KEYS[i]}`)}</span>
                <input type="time" style={s.timeInput} value={d.openTime} onChange={(e) => updateHour(i, 'openTime', e.target.value)} disabled={d.closed} />
                <span style={{ color: '#94A3B8' }}>-</span>
                <input type="time" style={s.timeInput} value={d.closeTime} onChange={(e) => updateHour(i, 'closeTime', e.target.value)} disabled={d.closed} />
                <label style={s.closedLabel}><input type="checkbox" checked={d.closed} onChange={(e) => updateHour(i, 'closed', e.target.checked)} /> {t('setup.step1.closed')}</label>
              </div>
            ))}

            <div style={{ ...s.footer, justifyContent: 'flex-end' }}>
              <button style={s.btn} onClick={submitShop} disabled={loading}>
                {loading ? t('setup.step1.creating') : t('setup.next')}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Services ───────────────────────────────────────────── */}
        {step === 2 && (
          <div style={s.card}>
            <h2 style={s.title}>{t('setup.step2.title')}</h2>
            <p style={{ color: '#94A3B8', fontSize: '0.9rem', margin: '0 0 1rem' }}>
              {t('setup.step2.subtitle')}
            </p>

            {services.length > 0 && services.map((svc) => (
              <div key={svc.id} style={s.svcItem}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#F8FAFC' }}>{svc.name}</div>
                  {svc.description && <div style={{ fontSize: '0.8rem', color: '#94A3B8' }}>{svc.description}</div>}
                </div>
                <button style={s.btnDanger} onClick={() => removeService(svc.id)}>{t('setup.step2.remove')}</button>
              </div>
            ))}

            <div style={{ marginTop: '1rem' }}>
              <label style={s.label}>{t('setup.step2.serviceName')}</label>
              <input style={s.input} value={svcName} onChange={(e) => setSvcName(e.target.value)} placeholder={t('setup.step2.serviceNamePlaceholder')} />
              <label style={s.label}>{t('setup.step2.description')}</label>
              <input style={s.input} value={svcDesc} onChange={(e) => setSvcDesc(e.target.value)} placeholder={t('setup.step2.descriptionPlaceholder')} />
              <button style={s.btnSmall} onClick={addService}>{t('setup.step2.addService')}</button>
            </div>

            <div style={s.footer}>
              <button style={s.btnOutline} onClick={() => setStep(1)}>{t('setup.back')}</button>
              <button style={s.btn} onClick={() => { setError(null); setStep(3); }}>{t('setup.next')}</button>
            </div>
          </div>
        )}

        {/* ── Step 3: Invite Staff ───────────────────────────────────────── */}
        {step === 3 && (
          <div style={s.card}>
            <h2 style={s.title}>{t('setup.step3.title')}</h2>
            <p style={{ color: '#94A3B8', fontSize: '0.9rem', margin: '0 0 1rem' }}>
              {t('setup.step3.subtitle')}
            </p>

            {invites.length > 0 && invites.map((inv, i) => (
              <div key={i} style={s.inviteItem}>
                <span style={{ flex: 1, fontSize: '0.9rem', color: '#F8FAFC' }}>{inv.email}</span>
                <span style={s.badge(roleBadgeColor[inv.role] || '#94A3B8')}>{inv.role}</span>
              </div>
            ))}

            <div style={{ marginTop: '1rem' }}>
              <label style={s.label}>{t('setup.step3.email')}</label>
              <input style={s.input} type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder={t('setup.step3.emailPlaceholder')} />
              <label style={s.label}>{t('setup.step3.role')}</label>
              <select style={{ ...s.select, width: '100%', boxSizing: 'border-box', marginBottom: '1rem' }} value={invRole} onChange={(e) => setInvRole(e.target.value)}>
                <option value="STAFF">{t('setup.step3.roleStaff')}</option>
                <option value="MANAGER">{t('setup.step3.roleManager')}</option>
              </select>
              <button style={s.btnSmall} onClick={sendInvite}>{t('setup.step3.invite')}</button>
            </div>

            <p style={s.note}>{t('setup.step3.inviteNote')}</p>

            <div style={s.footer}>
              <button style={s.btnOutline} onClick={() => { setError(null); setStep(2); }}>{t('setup.back')}</button>
              <button style={s.btn} onClick={() => navigate('/admin/queue')}>{t('setup.finishSetup')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
